import { useEffect, useRef } from 'react';
import { handleGlobalModalEscape } from '../utils/modalEscape';

/**
 * Focusable element selectors – mirrors the browser's default tab order.
 */
const FOCUSABLE_SELECTORS = [
  'a[href]:not([tabindex="-1"])',
  'button:not([disabled]):not([tabindex="-1"])',
  'input:not([disabled]):not([tabindex="-1"])',
  'select:not([disabled]):not([tabindex="-1"])',
  'textarea:not([disabled]):not([tabindex="-1"])',
  '[tabindex]:not([tabindex="-1"])',
  'details:not([tabindex="-1"])',
  'summary:not([tabindex="-1"])',
].join(', ');

/**
 * useFocusTrap
 *
 * Traps keyboard focus inside `modalRef` whenever `isActive` is true.
 * Also hides background content from assistive technology via aria-hidden.
 * Optionally calls `onEscape` when Escape is pressed (same as Cancel / X).
 *
 * @param {React.RefObject} modalRef  - ref attached to the modal content wrapper
 * @param {boolean}         isActive  - whether the trap should be active
 * @param {Function}        onEscape  - optional close/cancel handler for Escape key
 */
function useFocusTrap(modalRef, isActive, onEscape) {
  const previouslyFocusedRef = useRef(null);
  const onEscapeRef = useRef(onEscape);
  onEscapeRef.current = onEscape;

  useEffect(() => {
    if (!isActive) return;

    const modalEl = modalRef?.current;

    // ── 1. Store and restore previously focused element ──────────────────────
    previouslyFocusedRef.current = document.activeElement;

    // ── 2. Focus the first focusable element inside the modal ────────────────
    const getFocusable = () =>
      modalEl
        ? Array.from(modalEl.querySelectorAll(FOCUSABLE_SELECTORS))
        : [];

    const focusable = getFocusable();
    if (focusable.length > 0) {
      // Small timeout lets the modal render fully before focusing
      const focusTimer = setTimeout(() => {
        focusable[0].focus();
      }, 50);
    }

    // ── 3. Intercept Escape / Tab / Shift-Tab ────────────────────────────────
    const handleKeyDown = (e) => {
      const bpOverlay = document.querySelector('.bp-enroll-overlay');
      if (e.key === 'Escape' || e.keyCode === 27) {
        const nestedOpen = document.querySelector(
          '.pcc-form-overlay, .react-datepicker'
        );
        if (nestedOpen || bpOverlay) return;
        handleGlobalModalEscape(e, onEscapeRef.current);
        return;
      }

      if (e.key !== 'Tab') return;
      const scope = bpOverlay || modalEl;
      if (!scope) return;

      const focusable = Array.from(scope.querySelectorAll(FOCUSABLE_SELECTORS));
      if (focusable.length === 0) return;

      const firstEl = focusable[0];
      const lastEl = focusable[focusable.length - 1];
      const active = document.activeElement;

      // If focus is somehow completely outside the modal, pull it back in
      if (!scope.contains(active)) {
        const nested = active instanceof Element
          && active.closest('.pcc-form-overlay, .react-datepicker, .react-datepicker-popper, #app-date-input-portal, .icd-search-dropdown');
        if (nested) return;
        e.preventDefault();
        (e.shiftKey ? lastEl : firstEl).focus();
        return;
      }

      if (e.shiftKey) {
        // Shift-Tab: if on the first element, wrap to last
        if (active === firstEl || !scope.contains(active)) {
          e.preventDefault();
          lastEl.focus();
        }
      } else {
        // Tab: if on the last element, wrap to first
        if (active === lastEl || !scope.contains(active)) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    };

    // ── 4. Hide background from assistive tech ────────────────────────────────
    // Find all top-level children of <body> that are NOT ancestors of the modal
    const hiddenEls = [];
    const bodyChildren = Array.from(document.body.children);
    bodyChildren.forEach((child) => {
      if (child.id === 'app-date-input-portal') return;
      if (!child.contains(modalEl) && child !== modalEl) {
        child.setAttribute('aria-hidden', 'true');
        // Collect all focusable descendants and remove them from tab order
        const focusableDescendants = child.querySelectorAll(FOCUSABLE_SELECTORS);
        focusableDescendants.forEach((el) => {
          // Store original tabIndex so we can restore it
          el.dataset._origTabIndex = el.getAttribute('tabindex') ?? '';
          el.setAttribute('tabindex', '-1');
        });
        hiddenEls.push({ el: child, focusableDescendants });
      }
    });

    window.addEventListener('keydown', handleKeyDown, true);

    // ── 5. Cleanup ─────────────────────────────────────────────────────────────
    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);

      // Restore aria-hidden and tabIndex on background elements
      hiddenEls.forEach(({ el, focusableDescendants }) => {
        el.removeAttribute('aria-hidden');
        focusableDescendants.forEach((desc) => {
          const orig = desc.dataset._origTabIndex;
          if (orig === '') {
            desc.removeAttribute('tabindex');
          } else {
            desc.setAttribute('tabindex', orig);
          }
          delete desc.dataset._origTabIndex;
        });
      });

      // Restore focus to the element that had it before the modal opened
      if (
        previouslyFocusedRef.current &&
        typeof previouslyFocusedRef.current.focus === 'function'
      ) {
        previouslyFocusedRef.current.focus();
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isActive]);
}

export default useFocusTrap;
